import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogTitle,
    DialogDescription,
} from "./ui/dialog";
import { MapPin, X, ShieldCheck, Calendar as CalendarIcon, Loader2, ArrowRight, RotateCcw, MessageSquare, MessageCircle, Check } from "lucide-react";
import { ITEM_MODAL, AVAILABILITY } from "../lib/testIds";
import { requestRental, fetchItemAvailability } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import AskQuestionModal from "./AskQuestionModal";
import SignInRequiredDialog from "./SignInRequiredDialog";
import AvailabilityCalendar, {
    toIso,
    eachDay,
} from "./AvailabilityCalendar";

const CONDITION_LABEL = {
    new: "Brand new",
    "like-new": "Like new",
    good: "Good",
    fair: "Fair",
};

const PLATFORM_FEE_RATE = 0.1;

const formatMoney = (n) =>
    (Number(n) || 0).toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        maximumFractionDigits: 2,
    });

const formatRange = (from, to) => {
    const fmt = { month: "short", day: "numeric" };
    const yearOpts = { ...fmt, year: "numeric" };
    if (!from) return null;
    const end = to || from;
    const sameYear = from.getFullYear() === end.getFullYear();
    const sameMonth = sameYear && from.getMonth() === end.getMonth();
    if (!to || from.getTime() === end.getTime()) {
        return from.toLocaleDateString("en-US", yearOpts);
    }
    return `${from.toLocaleDateString("en-US", sameMonth ? { month: "short", day: "numeric" } : fmt)} → ${end.toLocaleDateString("en-US", yearOpts)}`;
};

export const ItemDetailModal = ({ item, open, onOpenChange }) => {
    const { user } = useAuth();
    const [range, setRange] = useState({ from: null, to: null });
    const [availability, setAvailability] = useState(null);
    const [availLoading, setAvailLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [message, setMessage] = useState("");
    const [confirmed, setConfirmed] = useState(null); // rental request once submitted
    const [askOpen, setAskOpen] = useState(false);
    const [signInPrompt, setSignInPrompt] = useState(false);

    useEffect(() => {
        if (!open || !item?.id) return;
        setRange({ from: null, to: null });
        setAvailability(null);
        setConfirmed(null);
        setMessage("");
        setSubmitting(false);
        setAvailLoading(true);
        fetchItemAvailability(item.id)
            .then((data) => setAvailability(data))
            .catch(() =>
                setAvailability({
                    blocked_dates: [],
                    booked_dates: [],
                    unavailable_dates: [],
                }),
            )
            .finally(() => setAvailLoading(false));
    }, [open, item?.id]);

    const days = useMemo(() => {
        if (!range.from) return 0;
        return eachDay(range.from, range.to || range.from).length;
    }, [range]);

    const breakdown = useMemo(() => {
        if (!item) return null;
        const d = Math.max(0, days);
        const base = +(d * (item.price_per_day || 0)).toFixed(2);
        // Deposit flows are hidden across the UI — always compute the total
        // as if the deposit is $0 so the checkout charge matches what the
        // renter sees. The backend still accepts item.deposit for legacy
        // data but the frontend never surfaces it.
        const deposit = 0;
        const total = +(base + deposit).toFixed(2);
        const platformFee = +(total * PLATFORM_FEE_RATE).toFixed(2);
        const payout = +(total - platformFee).toFixed(2);
        return { d, base, deposit, total, platformFee, payout };
    }, [item, days]);

    if (!item || !breakdown) return null;

    const handleCheckout = async () => {
        // Signed-out visitors get a friendly popup asking them to log in or
        // sign up — otherwise the backend just returns a bare 401 with no
        // guidance on what to do next.
        if (!user) {
            setSignInPrompt(true);
            return;
        }
        if (!range.from || days < 1) {
            toast.error("Pick a date range first", {
                description: "Tap a start date and an end date on the calendar.",
            });
            return;
        }
        try {
            setSubmitting(true);
            const rental = await requestRental({
                itemId: item.id,
                startDate: toIso(range.from),
                endDate: toIso(range.to || range.from),
                message: message.trim() || undefined,
            });
            setConfirmed(rental);
            toast.success("Request sent!", {
                description: `We'll notify ${item.owner_name || "the lender"} right away. You can track this under 'Pending approval' in Borrowing — once they accept, it moves to 'Pending payment' and you can complete checkout.`,
                duration: 8000,
            });
        } catch (err) {
            // The FastAPI backend sends validation errors as arrays and
            // domain errors as strings — normalize both so the toast is
            // always human-readable. Also log the raw error to the console
            // so it can be inspected in DevTools if the toast is unclear.
            console.error("[rental request failed]", err?.response?.status, err?.response?.data);
            let detail =
                err?.response?.data?.detail ||
                err?.message ||
                "Please try again in a moment.";
            if (Array.isArray(detail)) {
                detail = detail
                    .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
                    .filter(Boolean)
                    .join(" · ") || "Please double-check the dates and message.";
            } else if (typeof detail === "object") {
                detail = detail?.msg || JSON.stringify(detail);
            }
            toast.error("Couldn't send your request", { description: detail });
        } finally {
            setSubmitting(false);
        }
    };

    const isUnavailable =
        item.available === false ||
        item.status === "inactive" ||
        item.status === "rented";
    const rangeLabel = formatRange(range.from, range.to);

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent
                data-testid={ITEM_MODAL.root}
                className="max-h-[92vh] max-w-4xl overflow-y-auto rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-0 text-[#2e3220]"
            >
                <DialogTitle className="sr-only">{item.name}</DialogTitle>
                <DialogDescription className="sr-only">
                    Details for {item.name}
                </DialogDescription>

                <div className="grid gap-0 md:grid-cols-[1.05fr_1fr]">
                    <div className="relative aspect-[4/3] bg-[#E8E2D0] md:aspect-auto md:h-full">
                        {item.image_url && (
                            <img
                                src={item.image_url}
                                alt={item.name}
                                className="absolute inset-0 h-full w-full object-cover"
                                loading="lazy"
                            />
                        )}
                        <button
                            type="button"
                            onClick={() => onOpenChange(false)}
                            data-testid={ITEM_MODAL.close}
                            aria-label="Close"
                            className="absolute right-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-[#F4F1EA]/90 text-[#2e3220] backdrop-blur transition-colors hover:bg-[#F4F1EA]"
                        >
                            <X className="h-4 w-4" strokeWidth={2.5} />
                        </button>
                    </div>

                    <div className="p-7 md:p-8">
                        <div className="inline-flex items-center gap-1 rounded-full bg-[#737a3d]/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                            {item.category || "Item"}
                        </div>
                        <h2
                            data-testid={ITEM_MODAL.name}
                            className="mt-2 font-display text-3xl font-bold leading-tight text-[#2e3220]"
                        >
                            {item.name}
                        </h2>

                        <p
                            data-testid={ITEM_MODAL.description}
                            className="mt-3 text-sm leading-relaxed text-[#5a6142]"
                        >
                            {item.description}
                        </p>

                        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-[#5a6142]">
                            <span className="inline-flex items-center gap-1">
                                <ShieldCheck
                                    className="h-3.5 w-3.5 text-[#737a3d]"
                                    strokeWidth={2.5}
                                />
                                Condition: {CONDITION_LABEL[item.condition] || "Good"}
                            </span>
                            {item.location && (
                                <span className="inline-flex items-center gap-1">
                                    <MapPin className="h-3.5 w-3.5" />
                                    {item.location}
                                </span>
                            )}
                        </div>

                        <div
                            data-testid={ITEM_MODAL.owner}
                            className="mt-6 flex items-center gap-3 rounded-2xl border border-[#2e3220]/10 bg-[#E8E2D0]/60 p-3"
                        >
                            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#737a3d] font-display text-base font-bold text-[#F4F1EA]">
                                {item.owner_initial}
                            </span>
                            <div className="min-w-0">
                                <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                                    Listed by
                                </p>
                                <p className="truncate font-display text-sm font-bold text-[#2e3220]">
                                    {item.owner_name}
                                </p>
                            </div>
                            {item.owner_id && item.owner_id !== "anonymous" ? (
                                <Link
                                    to={`/neighbors/${item.owner_id}`}
                                    onClick={() => onOpenChange(false)}
                                    data-testid="item-detail-owner-profile-link"
                                    className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#F4F1EA] px-3 py-1.5 text-[10px] font-bold uppercase tracking-widest text-[#737a3d] hover:bg-white hover:text-[#2e3220]"
                                >
                                    View profile
                                    <ArrowRight className="h-3 w-3" strokeWidth={2.5} />
                                </Link>
                            ) : (
                                <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-[#F4F1EA] px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                                    Verified
                                </span>
                            )}
                        </div>

                        {/* --- Availability calendar --- */}
                        {confirmed ? (
                            <div className="mt-6 rounded-3xl border border-[#737a3d]/30 bg-[#737a3d]/10 p-6 text-center">
                                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-2xl bg-[#737a3d] text-[#F4F1EA]">
                                    <Check className="h-6 w-6" strokeWidth={2.5} />
                                </div>
                                <h3 className="font-display text-xl font-bold text-[#2e3220]">
                                    Request sent!
                                </h3>
                                <p className="mt-2 text-sm text-[#5a6142]">
                                    <span className="font-semibold text-[#2e3220]">
                                        {item.owner_name}
                                    </span>{" "}
                                    will review your dates and confirm. You&apos;ll get a
                                    payment link in <strong>Borrowing</strong> the moment
                                    they accept — no charge yet.
                                </p>
                                <button
                                    type="button"
                                    onClick={() => onOpenChange(false)}
                                    className="btn-secondary mt-5 !py-2 text-sm"
                                >
                                    Done
                                </button>
                            </div>
                        ) : (
                            <>
                        <div className="mt-6">
                            <div className="mb-2 flex items-center justify-between">
                                <p className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    Pick your dates
                                </p>
                                {range.from && (
                                    <button
                                        type="button"
                                        data-testid={AVAILABILITY.rangeReset}
                                        onClick={() => setRange({ from: null, to: null })}
                                        className="inline-flex items-center gap-1 rounded-full bg-[#2e3220]/5 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d] hover:bg-[#737a3d]/15"
                                    >
                                        <RotateCcw className="h-3 w-3" strokeWidth={2.5} />
                                        Reset
                                    </button>
                                )}
                            </div>
                            {availLoading ? (
                                <div className="h-72 animate-pulse rounded-3xl bg-[#E8E2D0]" />
                            ) : (
                                <AvailabilityCalendar
                                    mode="select"
                                    availability={availability}
                                    range={range}
                                    onRangeChange={setRange}
                                />
                            )}
                            <p
                                data-testid={AVAILABILITY.range}
                                className="mt-3 text-xs text-[#5a6142]"
                            >
                                {rangeLabel ? (
                                    <>
                                        <CalendarIcon className="mr-1 inline h-3 w-3" />
                                        <span className="font-semibold text-[#2e3220]">
                                            {rangeLabel}
                                        </span>{" "}
                                        — {days} day{days > 1 ? "s" : ""}
                                    </>
                                ) : (
                                    "Tap a start date, then an end date."
                                )}
                            </p>
                        </div>

                        {/* Optional message to the lender */}
                        {range.from && (
                            <label className="mt-5 block">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    <MessageSquare className="h-3 w-3" strokeWidth={2.5} />
                                    Message to {item.owner_name?.split(" ")[0] || "owner"} (optional)
                                </span>
                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value.slice(0, 500))}
                                    rows={2}
                                    placeholder="When can you pick it up? Any questions?"
                                    data-testid="rent-request-message"
                                    className="mt-1.5 w-full resize-none rounded-2xl border border-[#2e3220]/15 bg-white/70 px-3 py-2 text-sm text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                                />
                            </label>
                        )}

                        <div className="mt-6 flex items-end justify-between gap-4">
                            <p
                                data-testid={ITEM_MODAL.price}
                                className="font-display text-3xl font-bold text-[#737a3d]"
                            >
                                {formatMoney(item.price_per_day)}
                                <span className="ml-1 text-xs font-bold uppercase tracking-widest text-[#737a3d]/80">
                                    per day
                                </span>
                            </p>
                        </div>

                        {/* Fee breakdown */}
                        {breakdown.d > 0 ? (
                            <div
                                data-testid={ITEM_MODAL.feeBreakdown}
                                className="mt-3 space-y-1.5 rounded-2xl bg-[#2e3220]/5 px-4 py-3 text-sm"
                            >
                                <div className="flex justify-between text-[#5a6142]">
                                    <span>
                                        {formatMoney(item.price_per_day)} × {breakdown.d} day
                                        {breakdown.d === 1 ? "" : "s"}
                                    </span>
                                    <span>{formatMoney(breakdown.base)}</span>
                                </div>
                                {breakdown.deposit > 0 && (
                                    <div className="flex justify-between text-[#5a6142]">
                                        <span>Refundable deposit</span>
                                        <span>{formatMoney(breakdown.deposit)}</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-[10px] uppercase tracking-widest text-[#5a6142]/80">
                                    <span>Platform fee (10%) — kept by Lendly</span>
                                    <span>{formatMoney(breakdown.platformFee)}</span>
                                </div>
                                <div className="flex items-center justify-between border-t border-[#2e3220]/10 pt-2 text-base">
                                    <span className="font-display font-bold text-[#2e3220]">
                                        Charged now
                                    </span>
                                    <span className="font-display text-lg font-bold text-[#2e3220]">
                                        {formatMoney(breakdown.total)}
                                    </span>
                                </div>
                            </div>
                        ) : (
                            <p
                                data-testid={ITEM_MODAL.feeBreakdown}
                                className="mt-3 rounded-2xl bg-[#2e3220]/5 px-4 py-3 text-center text-xs text-[#5a6142]"
                            >
                                Pick a date range to see your total.
                            </p>
                        )}

                        <button
                            type="button"
                            onClick={handleCheckout}
                            disabled={
                                submitting || isUnavailable || (!!user && days < 1)
                            }
                            data-testid={ITEM_MODAL.requestBtn}
                            className="btn-primary mt-6 w-full disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {isUnavailable ? (
                                "Currently unavailable"
                            ) : submitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Sending request…
                                </>
                            ) : !user ? (
                                <>
                                    <MessageSquare className="h-4 w-4" strokeWidth={2.5} />
                                    Sign in to borrow
                                </>
                            ) : days < 1 ? (
                                "Pick your dates"
                            ) : (
                                <>
                                    <MessageSquare className="h-4 w-4" strokeWidth={2.5} />
                                    Request to borrow
                                </>
                            )}
                        </button>
                        <p className="mt-3 inline-flex items-center justify-center gap-1.5 text-center text-[11px] text-[#5a6142]">
                            <ShieldCheck className="h-3 w-3 text-[#737a3d]" />
                            No charge yet — payment happens after {item.owner_name?.split(" ")[0] || "the lender"} accepts.
                        </p>
                        <p className="mt-1 text-center text-[11px] text-[#5a6142]">
                            <CalendarIcon className="mr-1 inline h-3 w-3" />
                            Pickup is arranged directly with {item.owner_name} once confirmed.
                        </p>

                        <button
                            type="button"
                            data-testid="item-ask-question-btn"
                            onClick={() => setAskOpen(true)}
                            className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full border border-[#737a3d]/40 bg-transparent px-4 py-2.5 text-sm font-bold uppercase tracking-widest text-[#737a3d] hover:bg-[#737a3d]/5"
                        >
                            <MessageCircle className="h-4 w-4" strokeWidth={2.5} />
                            Ask a question
                        </button>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
            <AskQuestionModal
                item={item}
                open={askOpen}
                onOpenChange={setAskOpen}
            />
            <SignInRequiredDialog
                open={signInPrompt}
                onOpenChange={setSignInPrompt}
                action="rent this item"
            />
        </Dialog>
    );
};

export default ItemDetailModal;
